import React, { createContext, useContext, useCallback } from 'react';
import { useUser } from './UserContext';
import { filterPublicPlatformSlugs, isHiddenAdPlatform } from '../utils/adPlatforms';

const MULTI_CLIENTS_PLAN_SLUGS = ['agency', 'gestor_basic', 'gestor_pro'];

const UserPlanContext = createContext(null);

export const UserPlanProvider = ({ children }) => {
  const {
    plan: userPlanFromProfile,
    planFeatures,
    projectLimit,
    loading,
    refreshProfile,
  } = useUser();

  // Plano efetivo: null enquanto carrega, depois slug ou 'free'
  const userPlan = loading && userPlanFromProfile == null
    ? null
    : (userPlanFromProfile || 'free');

  const updateUserPlan = useCallback((_newPlan) => {
    // Plano vem do backend; após mudança (Stripe etc.) use refetch.
    refreshProfile();
  }, [refreshProfile]);

  // --- Helpers ---

  const isProUser = () => userPlan && userPlan !== 'free';
  const isFreeUser = () => userPlan === 'free';

  const canCreateProject = (currentProjectCount) => {
    if (projectLimit) {
      return projectLimit.current_count < projectLimit.max_projects;
    }
    if (planFeatures && planFeatures.limits) {
      const max = planFeatures.limits.max_projects || 1;
      const count = typeof currentProjectCount === 'number' ? currentProjectCount : 0;
      return count < max;
    }
    return true;
  };

  const getMaxProjects = () => {
    if (projectLimit) return projectLimit.max_projects;
    if (planFeatures && planFeatures.limits) return planFeatures.limits.max_projects || 1;
    return 1;
  };

  const getCurrentProjectCount = () => {
    if (projectLimit) return projectLimit.current_count;
    return 0;
  };

  const getRemainingProjects = () => {
    if (projectLimit) return projectLimit.remaining;
    if (planFeatures && planFeatures.limits) return planFeatures.limits.max_projects || 1;
    return 1;
  };

  const canCreateCampaign = (campaignLimitData) => {
    if (!campaignLimitData || campaignLimitData.length === 0) return true;
    const limitInfo = campaignLimitData[0];
    return limitInfo.allowed && limitInfo.remaining > 0;
  };

  const canUsePlatform = (platform) => {
    if (!planFeatures || !planFeatures.platforms) return true;
    if (isHiddenAdPlatform(platform)) return false;
    const platforms = planFeatures.platforms;
    const allowed = platforms.allowed || [];
    const normalized = platform.toLowerCase();
    if (!allowed.includes(normalized)) return false;
    const platformConfig = platforms[normalized];
    if (platformConfig && typeof platformConfig === 'object' && 'enabled' in platformConfig) {
      return platformConfig.enabled === true;
    }
    return true;
  };

  const getPlatformDetail = (platform) => {
    if (!planFeatures || !planFeatures.platforms) return null;
    const normalized = platform.toLowerCase();
    const config = planFeatures.platforms[normalized];
    if (!config || typeof config !== 'object') return null;
    return {
      enabled: config.enabled || false,
      max_ad_accounts: config.max_ad_accounts || 0,
      max_campaigns_per_cycle: config.max_campaigns_per_cycle || 0,
    };
  };

  const getAllowedPlatforms = () => {
    if (!planFeatures || !planFeatures.platforms) return ['google', 'meta'];
    const platforms = planFeatures.platforms;
    const allowed = platforms.allowed || [];
    return filterPublicPlatformSlugs(allowed.filter((p) => {
      const config = platforms[p];
      if (config && typeof config === 'object' && 'enabled' in config) {
        return config.enabled === true;
      }
      return true;
    }));
  };

  const canUseOptimizations = () => {
    if (!planFeatures || !planFeatures.optimizations) return false;
    return planFeatures.optimizations.enabled === true;
  };

  const canAccessFeature = (feature) => {
    if (planFeatures && planFeatures.features) {
      if (feature in planFeatures.features) {
        return planFeatures.features[feature];
      }
      if (feature === 'publicos') return false;
      if (feature === 'multi_clients') {
        return MULTI_CLIENTS_PLAN_SLUGS.includes(String(userPlan || '').toLowerCase());
      }
    }
    switch (feature) {
      case 'google_ads':
        return true;
      case 'advanced_analytics':
        return isProUser();
      case 'priority_support':
        return isProUser();
      case 'publicos':
        return false;
      case 'multi_clients':
        return MULTI_CLIENTS_PLAN_SLUGS.includes(String(userPlan || '').toLowerCase());
      default:
        return true;
    }
  };

  const canUsePublicos = () => canAccessFeature('publicos');

  const canUseSearchConsole = () => {
    if (planFeatures?.features?.search_console) return true;
    return planFeatures?.integrations?.search_console?.enabled === true;
  };

  const getSearchConsoleLimits = () => {
    const defaults = { max_properties: 0, data_retention_days: 0, enabled: false };
    if (!canUseSearchConsole()) return defaults;
    return {
      ...defaults,
      enabled: true,
      ...(planFeatures?.integrations?.search_console ?? {}),
    };
  };

  const canUseMultiClients = () => {
    const slug = String(userPlan || '').toLowerCase();
    if (slug === 'free') return false;

    if (planFeatures?.features && Object.prototype.hasOwnProperty.call(planFeatures.features, 'multi_clients')) {
      if (planFeatures.features.multi_clients) return true;
    }

    if (MULTI_CLIENTS_PLAN_SLUGS.includes(slug)) return true;

    // Segurança: qualquer plano pago com mais de 2 projetos tem acesso
    const count = projectLimit?.current_count ?? 0;
    if (count > 2) return true;

    return false;
  };

  const TEAM_COLLAB_PLAN_LIMITS = {
    super_pro: 2,
    gestor_pro: 1,
    agency: 4,
  };

  const canUseTeamCollaboration = () => {
    const slug = String(userPlan || '').toLowerCase();
    if (planFeatures?.features?.team_collaboration === true) {
      return getMaxMembersPerProject() > 0;
    }
    return slug in TEAM_COLLAB_PLAN_LIMITS;
  };

  const getMaxMembersPerProject = () => {
    const fromFeatures = planFeatures?.limits?.max_members_per_project;
    if (fromFeatures != null) {
      const n = parseInt(fromFeatures, 10);
      if (!Number.isNaN(n)) return n;
    }
    const slug = String(userPlan || '').toLowerCase();
    return TEAM_COLLAB_PLAN_LIMITS[slug] ?? 0;
  };

  const getPlanDisplayName = () => {
    const names = {
      'free': 'Free',
      'start': 'Start',
      'basic': 'Basic',
      'pro': 'Pro',
      'super_pro': 'Super Pro',
      'gestor_basic': 'Gestor Basic',
      'gestor_pro': 'Gestor Aspirante',
      'agency': 'Agência',
    };
    return names[userPlan] || 'Free';
  };

  const getPlanColor = () => {
    const colors = {
      'free': 'text.secondary',
      'start': 'success.main',
      'basic': 'info.main',
      'pro': 'primary.main',
      'super_pro': 'warning.main',
      'gestor_basic': 'info.main',
      'gestor_pro': 'primary.main',
      'agency': 'secondary.main',
    };
    return colors[userPlan] || 'text.secondary';
  };

  const value = {
    userPlan,
    planFeatures,
    projectLimit,
    loading,
    error: null,
    isProUser: isProUser(),
    isFreeUser: isFreeUser(),
    canAccessFeature,
    canUsePublicos: canUsePublicos(),
    canUseSearchConsole: canUseSearchConsole(),
    getSearchConsoleLimits,
    canUseMultiClients: canUseMultiClients(),
    canUseTeamCollaboration: canUseTeamCollaboration(),
    getMaxMembersPerProject,
    canCreateProject,
    canCreateCampaign,
    canUsePlatform,
    getPlatformDetail,
    getAllowedPlatforms,
    canUseOptimizations: canUseOptimizations(),
    getMaxProjects,
    getCurrentProjectCount,
    getRemainingProjects,
    getPlanDisplayName,
    getPlanColor,
    updateUserPlan,
    refetch: refreshProfile,
  };

  return (
    <UserPlanContext.Provider value={value}>
      {children}
    </UserPlanContext.Provider>
  );
};

export const useUserPlanContext = () => {
  const context = useContext(UserPlanContext);
  if (!context) {
    throw new Error('useUserPlanContext must be used within a UserPlanProvider');
  }
  return context;
};

export default UserPlanContext;
